import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { PropertyImage } from "../../data"
import { useGallery } from "../../providers"
import { useModalPresence, useFocusTrap } from "../../hooks"
import { useWidget } from "../../../../providers/widget";
import { GalleryWidgetConfig } from "../../config";
import { useGalleryTour } from "../../use-tour"
import { activeSectionFor, tourBarShouldShow, type TourSection } from "../../rooms"
import { getProxiedImageUrl } from "../../../../common/image-proxy"
import TwoRowThreeImageGrid from "./TwoRowThreeImageGrid"
import TwoColumnThreeImageGrid from "./TwoColumnThreeImageGrid"

/** Height of the pinned room bar; gallery.css sizes it from the same number. */
const TOUR_BAR_HEIGHT = 52
/** Room between the header (or the bar) and a room heading a jump lands on. */
const TOUR_LANDING_GAP = 16

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export default function FullScreenGrid() {
  const { options } = useWidget<GalleryWidgetConfig>()
  const { fullScreenImageIndex, setFullScreenImageIndex, setLightBoxImageIndex, lightBoxImageIndex } = useGallery()
  const { mounted, visible } = useModalPresence(fullScreenImageIndex !== undefined)
  // #1694: `allImages` is gallery order (the flat browser below); `tour` is the
  // by-room layout, whose tiles are numbered in tour order.
  const { images: allImages, tour, toTourIndex } = useGalleryTour()

  // Defer random layout to client to avoid hydration mismatch from Math.random()
  const [isClient, setIsClient] = useState(false)
  useEffect(() => { setIsClient(true) }, [])

  const layoutArchitecture = useMemo(() => {
    const chunkedBy3Images = allImages.reduce((acc, _, index) => {
      if (index % 3 === 0) {
        acc.push(allImages.slice(index, index + 3))
      }
      return acc
    }, [] as PropertyImage[][])

    if (chunkedBy3Images.length === 0) return []

    // Define layout types with weights
    const layoutTypes = [
      { type: 'twoRow', weight: 80 },
      { type: 'twoColumnLtr', weight: 10 },
      { type: 'twoColumnRtl', weight: 10 },
    ]

    const sequence: Array<{ type: string; images: PropertyImage[]; startIndex: number }> = []

    chunkedBy3Images.forEach((images, chunkIndex) => {
      const startIndex = chunkIndex * 3

      // Use default twoRow on server; randomize only on client
      if (!isClient || chunkIndex === 0) {
        sequence.push({ type: 'twoRow', images, startIndex })
        return
      }

      const random = Math.random() * 100
      let cumulativeWeight = 0
      let selectedType = 'twoRow'

      for (const layout of layoutTypes) {
        cumulativeWeight += layout.weight
        if (random <= cumulativeWeight) {
          selectedType = layout.type
          break
        }
      }

      sequence.push({ type: selectedType, images, startIndex })
    })

    return sequence
  }, [allImages, isClient])

  const imageGridRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(modalRef, fullScreenImageIndex !== undefined)

  // ---------------------------------------------------------------------------
  // Photo tour (#1694): the room index, one section per room, and the pinned
  // bar of room names (toolkit#806) that takes over once the index scrolls
  // under the header. All of it is scoped to `tour`; the flat browser renders
  // exactly as before when there is no tour.
  // ---------------------------------------------------------------------------
  const tourTitle = options.rooms.tourTitle
  const isTour = !!tour
  const pinned = isTour && tour.length > 1 && options.rooms.pinIndex
  const [barShown, setBarShown] = useState(false)
  const [activeSection, setActiveSection] = useState(0)
  const indexRef = useRef<HTMLElement | null>(null)
  const barRef = useRef<HTMLElement | null>(null)
  const sectionRefs = useRef<Array<HTMLElement | null>>([])
  const tickRef = useRef(false)
  const headingIdBase = useId()

  // Where a jump lands inside the tour: the target just under the header,
  // and under the bar too when the bar will be showing after the jump (the
  // index has scrolled away by then).
  const landingTop = useCallback((content: HTMLElement, offset: number) => {
    let top = offset - TOUR_LANDING_GAP
    if (pinned && indexRef.current) {
      const indexBottomAfter = indexRef.current.getBoundingClientRect().bottom - (top - content.scrollTop)
      if (tourBarShouldShow(indexBottomAfter, content.getBoundingClientRect().top)) {
        top -= barRef.current?.offsetHeight || TOUR_BAR_HEIGHT
      }
    }
    return Math.max(0, top)
  }, [pinned])

  // Openers hand over a GALLERY index (hero grid, carousel); the browser's
  // tiles carry their own index space, which is the tour order when the
  // photos are grouped by room. Map before scrolling into view. The "Show
  // all photos" buttons pass OPEN_AT_TOP, and the first photo needs no
  // scroll either: both open at the top, where the tour's room index is.
  const targetIndex = fullScreenImageIndex === undefined ? undefined : toTourIndex(fullScreenImageIndex)

  useEffect(() => {
    if (targetIndex === undefined || targetIndex <= 0) return
    const content = imageGridRef.current
    const image = content?.querySelector(`[data-global-index="${targetIndex}"]`)
    if (!content || !image) return
    const elementRect = image.getBoundingClientRect()
    const containerRect = content.getBoundingClientRect()
    const scrollTop = content.scrollTop || 0
    const offset = scrollTop + elementRect.top - containerRect.top
    // The flat browser keeps the offset it always had; the tour lands the
    // photo under the header and the bar.
    const targetPosition = isTour ? landingTop(content, offset) : offset - 85
    content.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    })
    // `mounted` must be a dep: on first open the effect fires before the
    // mount-gated grid renders (ref null), and without a rerun on mount the
    // grid opened at the top instead of the clicked image.
  }, [targetIndex, mounted, isTour, landingTop])

  // Handle ESC key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && fullScreenImageIndex !== undefined && lightBoxImageIndex === undefined) {
        console.log('Escape from full screen grid', event.key)
        handleClose()
      }
    }

    if (fullScreenImageIndex !== undefined) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullScreenImageIndex, lightBoxImageIndex])

  useEffect(() => {
    if (fullScreenImageIndex === undefined) {
      setBarShown(false)
      setActiveSection(0)
    }
  }, [fullScreenImageIndex])

  // Once per frame while the browser scrolls: show the bar when the index is
  // gone under the header, and mark the room in view (the last room whose
  // heading has reached the reading line under the header and the bar).
  const updateTourBar = useCallback(() => {
    tickRef.current = false
    const content = imageGridRef.current
    const index = indexRef.current
    if (!content || !index) return
    const headerBottom = content.getBoundingClientRect().top
    const show = tourBarShouldShow(index.getBoundingClientRect().bottom, headerBottom)
    setBarShown(show)
    const line = headerBottom + (show ? (barRef.current?.offsetHeight || TOUR_BAR_HEIGHT) : 0) + 24
    const tops = sectionRefs.current.map((el) => (el ? el.getBoundingClientRect().top : Number.POSITIVE_INFINITY))
    let active = activeSectionFor(tops, line)
    // A short last room never reaches the reading line; once the browser is
    // scrolled to its very end, that room is the one in view.
    if (content.scrollHeight > content.clientHeight && content.scrollTop + content.clientHeight >= content.scrollHeight - 1) {
      active = tops.length - 1
    }
    setActiveSection(Math.max(0, active))
  }, [])

  const onTourScroll = useCallback(() => {
    if (tickRef.current) return
    tickRef.current = true
    requestAnimationFrame(updateTourBar)
  }, [updateTourBar])

  // A tile or chip click: land the room's heading just under the header (and
  // the bar).
  const scrollToRoom = useCallback((i: number) => {
    const content = imageGridRef.current
    const block = sectionRefs.current[i]
    if (!content || !block) return
    const offset = block.getBoundingClientRect().top - content.getBoundingClientRect().top + content.scrollTop
    content.scrollTo({ top: landingTop(content, offset), behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }, [landingTop])

  const handleClose = () => {
    setFullScreenImageIndex(undefined)
  }

  const handleImageClick = (imageIndex: number) => {
    setLightBoxImageIndex(imageIndex)
  }

  if (!mounted) return null

  const renderTourSection = (section: TourSection<PropertyImage>, i: number) => {
    const headingId = `${headingIdBase}room-${i}`
    return (
      <section
        key={section.key || 'more'}
        ref={(el) => { sectionRefs.current[i] = el }}
        className="homerunner-gallery-tour-room"
        aria-labelledby={headingId}
      >
        <div className="homerunner-gallery-tour-room-head bg-card">
          <h3 id={headingId} className="homerunner-gallery-tour-room-title">{section.title}</h3>
        </div>
        <div className="homerunner-gallery-tour-room-photos">
          {section.blocks.map((block, bi) => (
            <div
              key={bi}
              className={`homerunner-gallery-mosaic homerunner-gallery-mosaic-${block.kind}${block.flip ? ' homerunner-gallery-mosaic-flip' : ''}`}
            >
              {block.idx.map((idx, k) => {
                const photo = section.photos[idx - section.start]
                if (!photo) return null
                const tall = block.kind === 'trio' && k === 2
                const full = block.kind === 'full'
                return (
                  <div key={idx} className={`homerunner-gallery-mosaic-cell gallery-image-container${tall ? ' homerunner-gallery-mosaic-tall' : ''}`}>
                    <button
                      type="button"
                      className="appearance-none border-0 bg-transparent p-0 m-0 block w-full h-full text-left cursor-pointer"
                      data-global-index={idx}
                      onClick={() => handleImageClick(idx)}
                    >
                      <img
                        src={getProxiedImageUrl(photo.url, full ? 1200 : 800, full ? 800 : 600)}
                        alt={photo.caption || `Property image ${photo.position}`}
                        className="homerunner-gallery-image"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="homerunner-gallery-hover-overlay gallery-hover-overlay"></div>
                    </button>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </section>
    )
  }

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
      className={`homerunner-gallery-fullscreen-modal fullscreen-modal bg-card text-card-foreground ${visible ? 'open' : 'closing'}${tour ? ' homerunner-gallery-fullscreen-modal--tour' : ''}${barShown ? ' homerunner-gallery-fullscreen-modal--bar' : ''}`}
      style={{ zIndex: 999 }}
    >
      {/* Back button - positioned on the left */}
      <button
        onClick={handleClose}
        autoFocus
        className="homerunner-gallery-fullscreen-back-button absolute top-4 left-4 z-10 transition-colors cursor-pointer p-2 rounded-full hover:bg-muted/50"
        aria-label="Back to gallery"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m12 19-7-7 7-7"/>
          <path d="M19 12H5"/>
        </svg>
      </button>

      {/* Pinned room index: one chip per room, under the header once the full
          index has scrolled away. Right after the header in the DOM, so it
          sits in the dialog's tab order before the grid; hidden, it is
          display:none and takes no focus. */}
      {pinned && tour && (
        <nav
          ref={barRef}
          className="homerunner-gallery-tour-bar bg-card text-card-foreground"
          aria-label="Jump to a room"
          hidden={!barShown}
        >
          <ul className="homerunner-gallery-tour-bar-list">
            {tour.map((section, i) => (
              <li key={section.key || 'more'}>
                <button
                  type="button"
                  className="homerunner-gallery-tour-chip"
                  aria-current={activeSection === i ? 'true' : undefined}
                  onClick={() => scrollToRoom(i)}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Images grid container with max width */}
      <div 
        className="homerunner-gallery-fullscreen-content w-full max-h-full overflow-y-auto scrollbar-hide overscroll-contain"
        ref={imageGridRef}
        onScroll={pinned ? onTourScroll : undefined}
      >
        {tour ? (
          <div className="homerunner-gallery-tour">
            {tour.length > 1 && (
              <nav ref={indexRef} className="homerunner-gallery-tour-index" aria-label={tourTitle}>
                <h2 className="homerunner-gallery-tour-heading">{tourTitle}</h2>
                <ul className="homerunner-gallery-tour-tiles">
                  {tour.map((section, i) => {
                    // The leftover photos get a 2x2 collage; rooms get their first photo.
                    const shots = section.rest && section.count >= 4 ? 4 : 1
                    return (
                      <li key={section.key || 'more'}>
                        <button type="button" className="homerunner-gallery-tour-tile" onClick={() => scrollToRoom(i)}>
                          <span className={`homerunner-gallery-tour-thumb${shots === 4 ? ' homerunner-gallery-tour-collage' : ''}`}>
                            {section.photos.slice(0, shots).map((photo, k) => (
                              <img key={k} src={getProxiedImageUrl(photo.url, 400, 267)} alt="" loading="lazy" decoding="async" />
                            ))}
                          </span>
                          <span className="homerunner-gallery-tour-label">{section.title}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </nav>
            )}
            {tour.map(renderTourSection)}
          </div>
        ) : layoutArchitecture.map((layout, index) => {
          const key = `layout-${index}-${layout.type}`
          
          switch (layout.type) {
            case 'twoRow':
              return <TwoRowThreeImageGrid containerWidth={700} onClick={handleImageClick} key={key} images={layout.images} startIndex={layout.startIndex} />
            case 'twoColumnLtr':
              return <TwoColumnThreeImageGrid containerWidth={700} onClick={handleImageClick} key={key} images={layout.images} direction="ltr" startIndex={layout.startIndex} />
            case 'twoColumnRtl':
              return <TwoColumnThreeImageGrid containerWidth={700} onClick={handleImageClick} key={key} images={layout.images} direction="rtl" startIndex={layout.startIndex} />
            default:
              return <TwoRowThreeImageGrid containerWidth={700} onClick={handleImageClick} key={key} images={layout.images} startIndex={layout.startIndex} />
          }
        })}
      </div>
    </div>
  )
}
